/**
 * Authentication Service
 * Magic Link auth with Stripe customer integration
 */

import { setCredits } from './stripe'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Session storage keys
const SESSION_KEY = 'emd_session'
const AUTH_STATE_KEY = 'emd_auth_state'

/**
 * Get current session from localStorage
 */
export function getSession() {
  try {
    const stored = localStorage.getItem(SESSION_KEY)
    if (!stored) return null

    const session = JSON.parse(stored)

    // Check if session is still valid (24h expiry)
    if (session.expiresAt && new Date(session.expiresAt) < new Date()) {
      clearSession()
      return null
    }

    return session
  } catch {
    return null
  }
}

/**
 * Set session data
 */
export function setSession(sessionData) {
  const session = {
    ...sessionData,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24h
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))

  // Sync credits to local store
  if (session.credits !== undefined) {
    setCredits(session.credits)
  }

  return session
}

/**
 * Clear session (logout)
 */
export function clearSession() {
  localStorage.removeItem(SESSION_KEY)
  localStorage.removeItem(AUTH_STATE_KEY)
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return getSession() !== null
}

/**
 * Send magic link to email
 */
export async function sendMagicLink(email) {
  try {
    const response = await fetch(`${API_BASE}/api/auth/send-magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Erreur lors de l\'envoi')
    }

    // Store email for verification page
    localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
      email,
      sentAt: new Date().toISOString(),
    }))

    return {
      success: true,
      message: data.message,
      // Dev mode: return link for testing
      devLink: data.devLink,
    }
  } catch (error) {
    console.error('Send magic link error:', error)
    return {
      success: false,
      error: error.message || 'Erreur réseau',
    }
  }
}

/**
 * Handle magic link callback (from URL params)
 */
export function handleMagicLinkCallback() {
  const params = new URLSearchParams(window.location.search)

  // Check for session data from successful auth
  const sessionParam = params.get('session')
  if (sessionParam) {
    try {
      const sessionData = JSON.parse(atob(sessionParam))
      setSession(sessionData)

      // Clean URL
      window.history.replaceState({}, '', window.location.pathname)

      return {
        success: true,
        session: sessionData,
      }
    } catch (error) {
      console.error('Parse session error:', error)
    }
  }

  // Check for auth error
  const authError = params.get('auth_error')
  if (authError) {
    // Clean URL
    window.history.replaceState({}, '', window.location.pathname)

    return {
      success: false,
      error: decodeURIComponent(authError),
    }
  }

  return null
}

/**
 * Get pending auth state (email waiting for magic link)
 */
export function getPendingAuthState() {
  try {
    const stored = localStorage.getItem(AUTH_STATE_KEY)
    if (!stored) return null

    const state = JSON.parse(stored)

    // Expire after 30 minutes
    const sentAt = new Date(state.sentAt)
    if (Date.now() - sentAt.getTime() > 30 * 60 * 1000) {
      localStorage.removeItem(AUTH_STATE_KEY)
      return null
    }

    return state
  } catch {
    return null
  }
}

/**
 * Sync local data to Stripe customer metadata
 */
export async function syncToStripe(data) {
  const session = getSession()
  if (!session?.customerId) {
    console.warn('Cannot sync: not authenticated')
    return { success: false, error: 'Non authentifié' }
  }

  try {
    const response = await fetch(`${API_BASE}/api/user/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: session.customerId,
        data,
      }),
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || 'Erreur sync')
    }

    return { success: true, metadata: result.metadata }
  } catch (error) {
    console.error('Sync to Stripe error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Fetch credits from Stripe (server-side truth)
 */
export async function fetchCreditsFromStripe() {
  const session = getSession()
  if (!session?.customerId) {
    return { success: false, error: 'Non authentifié' }
  }

  try {
    const response = await fetch(
      `${API_BASE}/api/user/credits?customerId=${session.customerId}`
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Erreur fetch')
    }

    // Update local credits
    setCredits(data.credits)

    // Update session
    const currentSession = getSession()
    if (currentSession) {
      setSession({ ...currentSession, credits: data.credits })
    }

    return { success: true, credits: data.credits }
  } catch (error) {
    console.error('Fetch credits error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update credits on Stripe (and local)
 */
export async function updateCreditsOnStripe(action, amount, reason) {
  const session = getSession()
  if (!session?.customerId) {
    return { success: false, error: 'Non authentifié' }
  }

  try {
    const response = await fetch(`${API_BASE}/api/user/credits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: session.customerId,
        action,
        amount,
        reason,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Erreur mise à jour')
    }

    // Update local credits
    setCredits(data.newCredits)

    // Update session
    const currentSession = getSession()
    if (currentSession) {
      setSession({ ...currentSession, credits: data.newCredits })
    }

    return {
      success: true,
      previousCredits: data.previousCredits,
      newCredits: data.newCredits,
    }
  } catch (error) {
    console.error('Update credits error:', error)
    return { success: false, error: error.message }
  }
}

export default {
  getSession,
  setSession,
  clearSession,
  isAuthenticated,
  sendMagicLink,
  handleMagicLinkCallback,
  getPendingAuthState,
  syncToStripe,
  fetchCreditsFromStripe,
  updateCreditsOnStripe,
}
